import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, ChartLineUp, Sparkle } from "@phosphor-icons/react";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1758518726869-01aff69a56e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMG9mZmljZSUyMHBlb3BsZXxlbnwwfHx8fDE3Nzg0NzQ0NzN8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/sales/dashboard", { replace: true });
  }, [user, navigate]);

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white" data-testid="login-page">
      {/* Left: Form */}
      <div className="flex flex-col px-8 sm:px-14 lg:px-20 py-10">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-md bg-blue-600 flex items-center justify-center">
            <ChartLineUp size={22} weight="bold" className="text-white" />
          </div>
          <div>
            <div className="font-heading text-xl font-bold tracking-tight text-slate-900 leading-none">
              SVRAF
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mt-0.5">
              CRM Suite
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center max-w-md w-full">
          <div className="w-full">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold mb-6">
              <Sparkle size={12} weight="fill" /> Secure access
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Run your pipeline.
              <br />
              Close more deals.
            </h1>
            <p className="mt-5 text-base leading-relaxed text-slate-600 font-body">
              Sign in to manage leads, track every customer interaction, and orchestrate orders —
              all from one clean, focused workspace.
            </p>

            <button
              onClick={handleGoogleSignIn}
              data-testid="google-signin-button"
              className="mt-10 w-full flex items-center justify-center gap-3 h-12 rounded-md border border-slate-200 bg-white text-slate-900 font-medium hover:bg-slate-50 hover:border-slate-300 transition-all duration-200 shadow-sm"
            >
              <FcGoogle size={22} />
              Continue with Google
            </button>

            <div className="mt-6 flex items-center gap-2 text-xs text-slate-500">
              <ShieldCheck size={14} weight="duotone" />
              Protected by Google OAuth 2.0. We never store your password.
            </div>

            <div className="mt-12 grid grid-cols-3 gap-4 text-center">
              {[
                ["Leads", "Unified"],
                ["Orders", "Trackable"],
                ["Activity", "Searchable"],
              ].map(([t, s]) => (
                <div key={t} className="border-l border-slate-200 pl-4 text-left first:border-0 first:pl-0">
                  <div className="text-sm font-semibold text-slate-900">{t}</div>
                  <div className="text-xs text-slate-500">{s}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="text-xs text-slate-400">© {new Date().getFullYear()} SVRAF CRM</div>
      </div>

      {/* Right: Hero image */}
      <div className="hidden md:block relative overflow-hidden">
        <img
          src={HERO_IMAGE}
          alt="Professional team collaboration"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-10 text-white">
          <div className="max-w-md backdrop-blur-md bg-white/10 border border-white/15 rounded-xl p-5">
            <div className="text-xs uppercase tracking-[0.22em] text-blue-200 font-semibold">
              Built for revenue teams
            </div>
            <div className="mt-2 font-heading text-2xl font-semibold leading-tight">
              &quot;Every conversation, every order, every dollar — in one workspace.&quot;
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
